import { Component, OnInit, Inject, AfterViewInit } from '@angular/core';
import {
  NavigationEnd, Router, NavigationStart,
  RouterEvent, NavigationCancel, NavigationError
} from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { AppSessionService, AppConfigService, AppAlertsService, defaultDisplayAlerts, ApiBrokerService, ApiBrokerInterceptor } from '@eucoms/common-components';
import { TranslateService } from '@ngx-translate/core';
import { constants } from './constants';
import { SharedService } from '@app/core/services/shared.service';
import { Alert } from '@eucoms/common-components';
import { GlobalBannerHelperService } from './core/services/global-banner-helper.service';
import { GoogleAnalyticsService } from './core/services/google-analytics-service';
import { ApiLoggingService } from './core/services/api-logging.service';
import { EudsFooterFullModule, EudsOpcoBrandingModule } from '@eucoms/common-components/design-system';
import { FeatureFlagService } from '@eucoms/common-components';
import { RecaptchaTokenService } from '@eucoms/common-components/recaptcha-v3';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'app';
  opco: string;
  showAppLoader: boolean;
  route: string;
  public opCoName: string;
  public opCoFooter: string;
  public gtmId: string;
  isEuds: boolean;
  isPageInIframe = false;
  isProvidingChatBot = constants.isProvidingChatBot;
  isProvidingGTM = constants.isProvidingGTM;
  fiveLinksFooter: boolean;
  footerNavLinks: any[];
  showFullOutageMap: any;
  showFullCard: any;

  get isShowingHeader() {
    return !this.isPageInIframe && !this.sharedService.routeThroughExceptions?.test(this.route);
  }
  get isShowingFooter() {
    return !this.isPageInIframe && !this.sharedService.routeThroughExceptions?.test(this.route);
  }

  get hasAlerts () {
    return !!this.alerts?.length;
  }

  get alerts () {
    return this.appAlertsService.alerts?.filter(alert => defaultDisplayAlerts.includes(alert.AlertType)
      && !this.globalBannerService.isGlobalBannerHasBeenClosed(alert.Message));
  }
  constructor(
    private appConfigService: AppConfigService,
    private appSessionService: AppSessionService,
    private appAlertsService: AppAlertsService,
    private router: Router,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private globalBannerService: GlobalBannerHelperService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private apiLoggingService: ApiLoggingService,
    private apiBroker: ApiBrokerService,
    private featureFlagService: FeatureFlagService,
    private reCaptchaTokenService: RecaptchaTokenService
  ) {
    this.opco = this.appConfigService.config.opCo;
    this.gtmId = this.appConfigService.config.googleTagManagerId || '';

    this.router.events.subscribe(this.navigationInterceptor.bind(this));
  }

  ngOnInit() {
    this.reCaptchaTokenService.registerRecaptchaInterceptor();
    const opco = this.appConfigService.config.opCo?.toLowerCase();
    this.translateService.setDefaultLang(opco);
    setTimeout(() => {
      this.showAppLoader = true;
    });

    // set page fav icon
    this.document.getElementById('opcoFavIcon')?.setAttribute('href', this.appConfigService.config.favIcon);

    // set header logo
    this.opCoName = this.appConfigService.config.logoName;

    // set footer message
    this.opCoFooter = this.appConfigService.config.footerMessage;

    this.isPageInIframe = this.sharedService.isPageInIframe();

    const urlPath = this.document.location.pathname.split('/');
    this.appSessionService.session.source = urlPath[urlPath.length - 1];
    this.setFooterNavLinks();

    if (this.appConfigService.config.experimentalApiBrokerUsesLoggingProxy?.toLowerCase() === 'true') {
      this.registerLoggingProxy();
    }
  }

  ngAfterViewInit() {
    this.resetBannerPersistencyOnInitLogin();
  }

  /**
   * register api broker logging proxy
   * @private
   */
  private registerLoggingProxy() {
    this.apiBroker.registerInterceptor({
      id: 'api-broker-logging-proxy',
      handleRequest: (request, next) => this.apiLoggingService.handleRequest(request, next),
      allowMultiple: false,
    } as ApiBrokerInterceptor);
  }

  /**
   * ensure that global banner will always be visible on every initial login
   * @private
   */
  private resetBannerPersistencyOnInitLogin(): void {
    if (this.globalBannerService.closedBannerResetCounter < 1) {
      this.globalBannerService.resetClosedBanner();
      this.globalBannerService.closedBannerResetCounter += 1;
    }
  }

  /**
   * triggers when global banner has been closed
   * @private
   */
  private onAlertClose(alert: Alert) {
    this.globalBannerService.saveToClosedBanner(alert.Message);
  }
  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: RouterEvent) {
    if (event instanceof NavigationStart) {
      this.showAppLoader = true;
      this.googleAnalyticsService.emitUserId();
    }

    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      const route = event.url.replace(/^\/?([^/]+)/, '$1');
      this.route = route;
      this.isEuds = this.sharedService.loadEUDSModule(this.route);

      // this is horrible, cludgy code that Bill made me put in here.
      if (!this.sharedService.firstNavigationComplete && !this.sharedService.routeThroughExceptions.test(route)) {
        // properly, this should be checking the reverse condition.
        this.sharedService.firstNavigationComplete = true;
        this.router.navigate(['/report']);
      }

      setTimeout(() => {
        this.showAppLoader = false;
      });
    }
  }

  private setFooterNavLinks() {
    this.translateService.get('footerNavLinks').subscribe((links) => {

      this.fiveLinksFooter = this.featureFlagService.hasFlag('experiencingAnOutage.newFooterLinks');

      if (this.fiveLinksFooter) {
        this.footerNavLinks = [
          { url: links.aboutUs, name: 'About Us' },
          { url: links.news, name: 'News' },
          { url: links.doingBusiness, name: 'Doing Business with Us' },
          { url: links.publicPostings, name: 'Public Postings' },
          { url: links.careers, name: 'Careers' }
        ];
      } else {
        this.footerNavLinks = [
          { url: links.aboutUs, name: 'About Us' },
          { url: links.news, name: 'News' },
          { url: links.privacy, name: 'Privacy' },
          { url: links.termsuse, name: 'Terms of Use' },
          { url: links.doingBusiness, name: 'Doing Business with Us' },
        ];
      }
    });

  }
}
